<template>
  <div ref="container" class="container">

    <error          v-if      = 'page === "error"'          :error='fatalError' :errorCode='fatalErrorCode' :origin="origin" :params='inParams'/>
    <Authentication v-else-if  = 'page === "authentication"' :authCode='authCode' @success="authenticated"/>
    <PaymentMethod  v-else-if    = 'page === "paymentmethod"'  :origin='origin' @stopLoader='stopLoader()' @setLoader='startLoader()' @methodSelected='methodSelected' @abort="cancel"/>
    <PaymentStart  v-else-if = 'page === "paymentstart"'  :origin='origin' :params='inParams'   @stopLoader='stopLoader()' @startLoader='startLoader()' @paymentStarted='onPaymentStarted' @abort="goToMethodSelection"/>
    <PaymentDetail  v-else-if = 'page === "paymentdetail"'  :origin='origin' :params='inParams'   @stopLoader='stopLoader()' @startLoader='startLoader()' @paymentDetail='onPaymentDetailCollected'  @abort="goToMethodSelection"/>
    <PaymentRedirect :key="redirectKey"  v-else-if = 'page === "paymentredirect"'  :origin='origin' :params='inParams'   @stopLoader='stopLoader()' @setLoader='startLoader()' @paymentResultCalled='onPaymentResultCalled'  @abort="goToMethodSelection" @goBack="goToPaymentStart"/>
    <PaymentResult  v-else-if = 'page === "paymentresult"'  :origin='origin' :params='inParams'   @stopLoader='stopLoader()' @setLoader='startLoader()' />
      

    <div class="loader-container">
      <loader v-if="loader" :color="'#062127'"/>
    </div>

  </div>
</template>

<script>
import Authentication from './Authentication'
import PaymentMethod from './methodSelection/PaymentMethod'
import PaymentStart from './start/PaymentStart'
import PaymentDetail from './PaymentDetail'
import PaymentRedirect from './PaymentRedirect'
import PaymentResult from './result/PaymentResult'
import Loader from '@/components/Loader.vue'
import Error from '@/views/error'
import Detect from 'mobile-device-detect'
import valueMappings from '@/mixins/valueMappings'
import postMessageMixin from '@/mixins/postMessageMixin'
import Debounce from '@/utils/debounce'
import { mapState } from 'vuex'

export default {
  name: 'Payment',
  props: {
    config: {
      type: Object,
      default: () => ({})
    },
    params: {
      type: Object,
      default: () => ({})
    },
    mode: {
      type: String,
      default: '1'
    },
    version: {
      type: String,
      default: ''
    },
    origin: {
      type: String,
      default: ''
    },
    authCode: {
      type: String,
      default: ''
    }
  },
  components: { Authentication, PaymentMethod, PaymentStart, PaymentDetail, PaymentRedirect, PaymentResult, Error, Loader },
  inject: ['$message'],
  mixins: [valueMappings, postMessageMixin],
  data () {
    return {
      page: 'authentication',
      loader: true,
      redirectKey: 0,
      inParams: {},
      contentHeight: 0,
      contentWidth: 0,
      contentHeightObserver: null,      
      debounce: new Debounce()
    }
  },
  mounted: function () {
    if (this.$refs.container) {

      const targetNode = this.$refs.container
      const config = { attributes: true, childList: true, subtree: true }
      const callback = this.sizeChangedCallback
      this.contentHeightObserver = new MutationObserver(callback)
      this.contentHeightObserver.observe(targetNode, config)
    }
  },
  computed: {
    ...mapState(['fatalError', 'fatalErrorCode', 'sizeUpdate']),
    appId () {
      return this.isMobile ? 'appMobile' : 'appWeb'
    },
    isMobile () {
      return Detect.isMobile
    }
  },
  created () {
    this.inParams = this.params
  },
  beforeDestroy () {
    if (this.contentHeightObserver) this.contentHeightObserver.disconnect()
  },
  methods: {
    startLoader () {
      this.loader = true
    },
    stopLoader () {
      this.loader = false
    },
    authenticated () {
      this.inParams.authenticated = true
      this.page = 'paymentmethod'
    },
    methodSelected (info) {
      this.startLoader()
      this.inParams.methodTypeId = Number(info.methodTypeId)
      this.inParams.methodId = info.methodId
      this.inParams.aliasId = info.aliasId
      this.inParams.aliasStep = info.aliasStep
      this.inParams.registerAlias = info.registerAlias
      this.inParams.aliasLabel = info.aliasLabel
      this.inParams.mkpData = info.mkpData
      this.inParams.startNeedsInput = info.startNeedsInput
      this.inParams.doNotAddDetails = info.doNotAddDetails
      this.page = 'paymentstart'
    }, 
    onPaymentStarted (info) {
      //redirectInd: 0 no redirection / 1 redirect in inner iframe / 2 redirect parent top page
      if (this.isNotDefined(info.redirectInd)) this.inParams.redirectInd =this.isSDD(this.inParams.methodTypeId) ? false : true
      else this.inParams.redirectInd = this.isTrueValue(info.redirectInd)
      this.inParams.parentRedirect = info.redirectInd === 2 || info.redirectInd === '2'
      this.inParams.redirectUrl = info.redirectUrl
      this.inParams.frameHeight = info.frameHeight || null
      this.inParams.frameWidth = info.frameWidth || null
      this.redirectKey++
      this.inParams.transactionData = info // could create an object with only relevant fields, being lazy
      if (this.inParams.parentRedirect) this.leaveIframeOnRedirect(info.redirectUrl)
      else if (this.inParams.redirectInd === false) this.page = 'paymentresult'
      else this.page = 'paymentdetail'
    },
    onPaymentResultCalled (info) {
      if (this.isNotDefined(info.endInd)) this.inParams.endInd = true
      else this.inParams.endInd = this.isTrueValue(info.endInd)
      this.inParams.redirectUrl = info.redirectUrl
      this.inParams.frameHeight = info.frameHeight || null
      this.inParams.frameWidth = info.frameWidth || null
      this.inParams.transactionData = info
      this.redirectKey++
      if (this.inParams.endInd === true) this.page = 'paymentresult'
      else this.page = 'paymentredirect'
    },
    onPaymentDetailCollected (info) {
      this.inParams.detail = info
      this.page = 'paymentredirect'
    },
    next (p) {
      this.startLoader()
      this.page = p
    },
    previous () {
      this.startLoader()
      // delete form data when changing payment method
      this.params.lastUserEntry = null
      this.inParams = this.params
      this.page = 'paymentmethod'
    },
    goToMethodSelection () {
      this.previous()
    },
    goToPaymentStart () {
      this.startLoader()
      this.page = 'paymentstart'
    },
    cancel () {
      this.postMessageToParent({ messageType: 'abort', resultCode: 'done'}, this.inParams, this.origin)
    },
    sizeChangedCallback: function() {
    let _this = this
      setTimeout(() => {
        let height = _this.$refs.container.clientHeight
        let width = _this.$refs.container.clientWidth
        let heightChanged = height >0 && (height !== _this.contentHeight)
        let widthChanged = width >0 && (width !== _this.contentWidth)
        _this.contentHeight = height
        _this.contentWidth = width
        if (heightChanged || widthChanged) _this.debounce.debounce(10, function () {
          _this.sendSizeChange(widthChanged)
        })
      }, 200)
    },
    sendSizeChange (widthChanged) {
      let message = { messageType: 'resize', height: this.contentHeight}
      if (widthChanged) message.width = this.contentWidth
      window.parent.postMessage(JSON.stringify(message), this.origin)
    },
    leaveIframeOnRedirect (url) {
      window.top.location.href = url 
    }
  },
  watch: {
    fatalError(newValue) {
      if (newValue) {
        this.stopLoader()
        this.page = 'error'
      }
    },
    sizeUpdate () {
      this.sizeChangedCallback()
    }
  },
}
</script>

<style lang='scss'>

.loader-container {
  position: absolute;
  left: 50%;
  top: 20%;
  transform: translate(-50%, -50%);
  z-index: 2000;
}
</style>
